import React from 'react'
import { useMemo, useState } from 'react'
import { createContext, useContextSelector } from 'use-context-selector'

import { NOOP } from 'constants/commonConstans'

import type { TestGroupingStateType } from './testGroupingTypes'

type SetElementState = (value: TestGroupingStateType) => void

interface ITestGroupingFormValueContext {
  form?: 'root' | 'image' | 'answer'
  groupIndex?: number
  groupName?: string
  answerIndex?: number
  answerName?: string
  setContext: React.Dispatch<
    React.SetStateAction<Omit<ITestGroupingFormValueContext, 'setContext' | 'setElementState'>>
  >
  setElementState?: SetElementState
}

const DEFAULT_CONTEXT: Omit<ITestGroupingFormValueContext, 'setContext' | 'setElementState'> = {
  form: 'root',
  groupName: '',
  groupIndex: 0,
  answerName: '',
  answerIndex: 0,
}

const TestGroupingFormValueContext = createContext<ITestGroupingFormValueContext>({
  ...DEFAULT_CONTEXT,
  setContext: NOOP,
})

interface ITestGroupingFormValueProviderProps {
  children: React.ReactNode
  setElementState?: SetElementState
}

const TestGroupingFormValueProvider: React.FC<ITestGroupingFormValueProviderProps> = ({
  children,
  setElementState,
}) => {
  const [value, setValue] = useState<Partial<ITestGroupingFormValueContext>>(DEFAULT_CONTEXT)
  const context = useMemo(
    () => ({ ...value, setContext: setValue, setElementState }),
    [value, setElementState],
  )

  return (
    <TestGroupingFormValueContext.Provider value={context}>
      {children}
    </TestGroupingFormValueContext.Provider>
  )
}

export const useGroupingFormValueContext = () => {
  return useContextSelector(TestGroupingFormValueContext, (c) => c)
}

export const useGroupingFormValueUpdateForm = () => {
  return useContextSelector(TestGroupingFormValueContext, (c) => c.setContext)
}

export const useGroupingFormValueSetElementState = () => {
  return useContextSelector(TestGroupingFormValueContext, (c) => c.setElementState)
}

export default React.memo(TestGroupingFormValueProvider) as typeof TestGroupingFormValueProvider
